import { TFunction } from 'react-i18next'
import { Route } from '../../routes/Route'
import { isSubPath } from './useNavigationMode'

/**
 * A route shows in the menu when it's named and not hidden. A grouping route without a page of its own
 * only shows while the user can see at least one page under it, so it never links to an empty page.
 */
export const isVisible = (route: Route): boolean =>
  !route.hideInMenu && !!route.name && (!!route.element || !!route.routes?.some(isVisible))

/** An index route shares its parent's path, so its label has its own translation key. */
export const menuLabel = (route: Route, t: TFunction) =>
  t(route.index ? `menu:${route.path}/index` : `menu:${route.path}`, route.name as string)

/** First page under a route, skipping submenu nodes that only group other pages. */
export const firstPagePath = (route: Route): string => {
  const firstChild = route.routes?.find(isVisible)
  return firstChild ? firstPagePath(firstChild) : route.path
}

/**
 * Named routes from the top of the route tree down to the deepest one containing the pathname, e.g.
 * settings › company › financial › fiscal years. Detail pages without a named route of their own (e.g.
 * /superuser/users/123) end at the closest named route above them. Index routes share their parent's
 * path, so they are skipped rather than repeated.
 */
export const findRouteTrail = (routes: Route[], pathname: string, parentPath?: string): Route[] => {
  const candidates = routes.filter(route => route.path && route.path !== parentPath)
  const match = candidates
    .filter(route => route.name && isSubPath(pathname, route.path))
    .sort((a, b) => b.path.length - a.path.length)[0]

  if (!match) {
    // Pathless wrapper routes (guards, layouts) don't show in the trail, but their children do.
    for (const wrapper of routes.filter(route => !route.path && route.routes)) {
      const trail = findRouteTrail(wrapper.routes ?? [], pathname, parentPath)
      if (trail.length > 0) return trail
    }
    return []
  }

  return [match, ...findRouteTrail(match.routes ?? [], pathname, match.path)]
}
